import { action, observable } from 'mobx';
import api from 'core/util/api';
import { isEmpty, mapKeys, upperFirst } from 'lodash';

const PROVIDER_ATTR = 'providers';
const SUPPORT_CREDENTIALS_ATTR = 'support_credentials';
const DEFAULT_PROVIDER_ATTR = 'default';

class AiStore {
  @observable.ref
  allAllowedModels = {};

  @observable
  loading = true;

  @action
  initialize() {
    const { $app } = this.store;
    if (!$app.isSubtenant) {
      this.loadModels();
    }
  }

  @action
  loadModels = async () => {
    const hasPermission = this.store.$auth.hasPermission('aiModels', { overrideForSudo: false });
    if (hasPermission) {
      if (!isEmpty(this.allAllowedModels)) {
        this.loading = false;
      } else {
        api.get('/api/ui/ai/models', { showErrorToast: false }).then(
          action((models) => {
            this.allAllowedModels = models;
            this.loading = false;
          })
        );
      }
    } else {
      this.loading = false;
    }
  };

  isDefaultProvider = (provider) => provider === DEFAULT_PROVIDER_ATTR;

  getAllowedModels = (tier) => (tier ? this.allAllowedModels[tier] || {} : this.allAllowedModels);

  getProviderInfo = (tier, provider) => {
    const allowedModels = this.getAllowedModels(tier);

    if (isEmpty(allowedModels)) {
      return undefined;
    }

    return this.isDefaultProvider(provider)
      ? allowedModels[provider]
      : allowedModels[PROVIDER_ATTR]?.find(({ id }) => id === provider);
  };

  defaultProviderName = (tier) =>
    // note: assuming provider_id is in set of existing providers for tier
    this.getProviderInfo(tier, this.getAllowedModels(tier)[DEFAULT_PROVIDER_ATTR]?.provider_id)?.name;

  isCredentialSupported = (tier, provider) => {
    const providerInfo = this.getProviderInfo(tier, provider);
    return this.isDefaultProvider(provider) || (providerInfo && providerInfo[SUPPORT_CREDENTIALS_ATTR]);
  };

  get tiers() {
    return Object.keys(this.allAllowedModels).map((tier) => ({ ...this.allAllowedModels[tier] }));
  }

  getProviderOptions = (tier) => {
    const allowedModels = this.getAllowedModels(tier);
    const options = [{ value: DEFAULT_PROVIDER_ATTR, label: 'Kentik default' }];
    if (allowedModels) {
      Object.values(allowedModels[PROVIDER_ATTR] || {}).forEach(({ id, name }) => {
        options.push({ value: id, label: name });
      });
    }

    return options;
  };

  getModelOptions = (tier, provider) => {
    const options = [];
    const providerInfo = this.getProviderInfo(tier, provider);
    if (providerInfo) {
      const models = this.isDefaultProvider(provider) ? [providerInfo] : providerInfo.models;
      models?.forEach(({ id, name }) => {
        options.push({ label: name, value: id });
      });
    }

    return options;
  };

  getRegionOptions = (tier, provider, model) => {
    const providerInfo = this.getProviderInfo(tier, provider);
    const options = [];
    if (providerInfo) {
      const { models } = providerInfo;
      (models?.find(({ id }) => id === model)?.regions || []).forEach((region) => {
        options.push({ label: region, value: region });
      });
    }
    return options;
  };

  getFieldsByTier = (tier) => {
    const baseFields = {
      provider: {
        label: 'Provider',
        defaultValue: DEFAULT_PROVIDER_ATTR,
        options: this.getProviderOptions(tier)
      },
      region: {
        label: 'Region',
        messages: { required: `${upperFirst(tier)} Model: Please select a region.` }
      },
      model: {
        label: 'Model',
        rules: 'required',
        messages: { required: `${upperFirst(tier)} Model: Please select a model.` }
      },
      credential: {
        label: 'API Token',
        messages: { required: `${upperFirst(tier)} Model: Please select an API Token.` }
      },
      keyTypes: {
        defaultValue: ''
      }
    };

    return mapKeys(baseFields, (value, key) => `${tier}Model.${key}`);
  };

  get fields() {
    const fields = {};
    this.tiers.map(({ name }) => Object.assign(fields, this.getFieldsByTier(name)));
    return fields;
  }
}

export default new AiStore();
